import { ReactNode, useState } from "react";

export interface SettingsTab {
  id: string;
  label: string;
  content: ReactNode;
}

/**
 * The settings-page layout (27 Sep debug notes item 13): left-hand tabs on desktop, collapsing to a
 * single scroll on mobile, where a sidebar costs more than it gives.
 *
 * Both settings pages had grown into one long scroll of unrelated sections -- money, consent, a
 * password, a theme, and the account-deletion button, one after another with nothing to jump between
 * them. The coach's client page has the same shape of problem but is its own, more involved page and is
 * deliberately not touched here.
 *
 * Every tab's content renders into the DOM all the time; only the CSS for the one `.hidden-tab` class
 * changes between breakpoints (see index.css). That is what makes "all sections, one scroll" on mobile
 * require no separate code path -- it's the same markup as desktop, just not filtered down to one pane.
 */
export function SettingsTabs({ tabs, defaultTabId }: { tabs: SettingsTab[]; defaultTabId?: string }) {
  const [activeId, setActiveId] = useState(defaultTabId ?? tabs[0]?.id ?? "");

  return (
    <div className="settings-tabs">
      <nav className="settings-tabs-nav" aria-label="Settings sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={tab.id === activeId ? "settings-tab active" : "settings-tab"}
            aria-current={tab.id === activeId ? "true" : undefined}
            onClick={() => setActiveId(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>
      <div className="settings-tabs-content">
        {tabs.map((tab) => (
          <section
            key={tab.id}
            className={tab.id === activeId ? "settings-tab-panel" : "settings-tab-panel hidden-tab"}
          >
            {tab.content}
          </section>
        ))}
      </div>
    </div>
  );
}
